import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  ArrowRight,
  Code,
  Smartphone,
  Brain,
  Coffee,
  Palette,
  TestTube,
} from "lucide-react";
import { mockData } from "../mock";

const domainIcons = {
  1: Code,
  2: Brain,
  3: Smartphone,
  4: Coffee,
  5: Palette,
  6: TestTube,
};

export const Domains = () => {
  return (
    <section id="domains" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-4xl font-bold text-gray-900">
            Pick a Domain. Build 2–4 Work-Ready Projects.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Each specialization comes with real-world projects, industry tools,
            and direct pathways to hiring roles.
          </p>
        </div>

        {/* Domains Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {mockData.domains.map((domain) => {
            const IconComponent = domainIcons[domain.id];

            return (
              <Card
                key={domain.id}
                className="group bg-white hover:shadow-xl transition-all duration-300 
                   hover:-translate-y-1 overflow-hidden
                   h-full min-h-[560px] flex flex-col"
              >
                {/* Domain Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={domain.image}
                    alt={domain.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30">
                      {domain.stack}
                    </Badge>
                  </div>
                </div>

                <CardHeader className="pb-3">
                  <CardTitle className="text-xl text-gray-900">
                    {domain.title}
                  </CardTitle>
                </CardHeader>

                {/* 🔒 LOCKED CONTENT AREA */}
                <CardContent className="flex flex-col flex-1">
                  {/* ===== MIDDLE FIXED CONTENT AREA ===== */}
                  <div className="flex-1 grid grid-rows-[auto_auto_auto] gap-4 min-h-[260px]">
                    {/* Projects */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Projects You'll Build:
                      </h4>
                      <ul className="space-y-1 max-h-[90px] overflow-hidden">
                        {domain.projects.slice(0, 3).map((project, idx) => (
                          <li
                            key={idx}
                            className="text-sm text-gray-600 flex items-center gap-2"
                          >
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" />
                            {project}
                          </li>
                        ))}
                        {domain.projects.length > 3 && (
                          <li className="text-sm text-gray-500">
                            +{domain.projects.length - 3} more project
                          </li>
                        )}
                      </ul>
                    </div>

                    {/* Roles */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Target Professional Roles:
                      </h4>
                      <div className="flex flex-wrap gap-2 max-h-[64px] overflow-hidden">
                        {domain.roles.map((role, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="text-xs"
                          >
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Tools */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Tech Stack:
                      </h4>
                      <div className="flex flex-wrap gap-1 max-h-[56px] overflow-hidden">
                        {domain.tools.slice(0, 5).map((tool, idx) => (
                          <span
                            key={idx}
                            className="flex items-center gap-2
                       text-xs bg-gray-100 text-gray-700
                       px-2 py-1 rounded"
                          >
                            <img
                              src={tool.icon}
                              alt={tool.name}
                              className="w-4 h-4 object-contain"
                            />
                            {tool.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ===== BUTTON ZONE (ALWAYS SAME PLACE) ===== */}
                  <Button
                    className="w-full bg-slate-600 hover:bg-slate-700 text-white mt-4"
                    onClick={() =>
                      document
                        .getElementById("hero-form")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                  >
                    See Projects & Curriculum
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom Section */}
        <div className="text-center mt-16 space-y-6">
          <h3 className="text-2xl font-semibold text-gray-900">
            All Programs Include:
          </h3>

          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto">
                <Code className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-semibold">Live Project Reviews </h4>
              <p className="text-sm text-gray-600">
                Weekly project and code reviews with structured mentor feedback
              </p>
            </div>

            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto">
                <Brain className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold">Industry Mentors</h4>
              <p className="text-sm text-gray-600">
                Learn directly from working professionals with real-world
                experience{" "}
              </p>
            </div>

            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto">
                <Coffee className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold">Real Development Environment </h4>
              <p className="text-sm text-gray-600">
                Agile workflow with stand-ups, sprints, and production-style
                practices{" "}
              </p>
            </div>

            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto">
                <ArrowRight className="w-6 h-6 text-orange-600" />
              </div>
              <h4 className="font-semibold">Career & Job Support </h4>
              <p className="text-sm text-gray-600">
                Resume guidance, interview prep, and referral opportunities{" "}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
